export default function(ngModule) {
    ngModule.factory('ProductReservationPool', ProductReservationPoolFactory).name;
}

function ProductReservationPoolFactory($q, $http, $rootScope, MetaData, Product, syncHttp, LaravelValidationRules, $filter) {

    return function ProductReservationPoolFactory(childReservations, ticketReservation) {
        const pool = [];
        const children = [];
        const ticket = ticketReservation;
        _.forEach(childReservations.Product, (reservations, productId) => {
            pool.push(...reservations.map(guid => {
                return { productId, guid, metaData: {} };
            }));
            children.push(...reservations);
        });

        /**
         * @ngdoc service
         * @name eventix.common.models.ProductReservationPool
         * @description
         * A pool of product reservations, manages data within a ticketReservation
         * @param {Object} childReservations The child reervations from the ticket
         * @param {TicketReservation} ticketReservation Pareth of this pool
         * @example
         * ```
         * new ProductReservationPool({
         *     Product: {
         *          productGuid: ['reservation-guid']
         *     }
         * }, ticketReservation);
         * ```
         */
        return {
            [Symbol.iterator]: each,
            each, filter, has,
            reserve, release, releaseAll,
            satisfies, invalid,
            getTicketReservation,
            getPersonMetaDataFor, getProductMetaDataFor,
            getPersonMetaDataValuesFor, getProductMetaDataValuesFor,
            setMetaDataValue
        };

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#reserve
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Reserve a product
         * @param {String} productId Product GUID to reserve
         * @returns {Promise<String>} Resolves with the reservation guid
         */
        function reserve(productId) {
            const ticketId = ticket.ticket_guid;
            return $http.get(`/${ticket.shop.guid}/reserve/ticket/${ticketId}/product/${productId}`)
                .then((response) => {
                    pool.push({ productId, guid: response.data.reservation, metaData: {} });
                    $rootScope.$broadcast('TicketReservationChanged', ticket);
                    ticket.constructor.broadcastAPIMessage(response);
                    return response.data.reservation;
                }, error => {
                    ticket.constructor.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#release
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Release a product reservation
         * @param {String} productId A product guid to release
         * @param {String} [guid] Reservation guid if you want to release a very specific product
         * @param {Boolean} [sync=false] Whether to perform the request synchronously
         * @returns {Promise} Resolves when released
         * @emits TicketReservationChanged with a reference to the TicketReservation
         */
        function release(productId, guid, sync) {
            if(!guid) {
                const reservations = filter(productId);
                if(!reservations.length)
                    return $q.reject(new Error('No reservations for found'));
                guid = reservations[0].guid;
            }
            const url = `/${ticket.shop.guid}/reserve/product/${productId}/${guid}`;
            let promise;
            if(sync)
                promise = syncHttp('DELETE', url);
            else
                promise = $http.delete(url);
            return promise.then(response => {
                _.pull(children, guid);
                _.remove(pool, { guid, productId });
                $rootScope.$broadcast('TicketReservationChanged', ticket);
                ticket.constructor.broadcastAPIMessage(response);
                return $q.resolve(response);
            }, error => {
                ticket.constructor.broadcastAPIMessage(error);
                return $q.reject(error);
            });
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#releaseAll
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Release all non-child product reservations
         *
         * Direct children are auto-released when the ticket is released, so a manual release is not required.
         * @param {Boolean} sync Whether to release the products using a sync http request
         * @returns {Promise} Resolves when all additional products are released
         */
        function releaseAll(sync) {
            let promises = [];
            for(let reservation of pool) {
                if(!children.includes(reservation.guid))
                    promises.push(this.release(reservation.productId, reservation.guid, sync));
            }
            return $q.all(promises);
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#each
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Lazy iterator over the set of product reservations
         *
         * @yield {Object} Product reservations `{guid, productId, metaData}`
         */
        function * each() {
            for(let reservation of pool)
                yield reservation;
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#filter
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Find all reservations for a product
         * @param {String} productId Guid of a product
         * @returns {Array<Object>} Array of product reservations `{guid, productId, metaData}`
         */
        function filter(productId) {
            return pool.filter(reservation => reservation.productId === productId);
        }

        /**
         * Check if pool has one or more reservations for a product
         * @param {String} productId Product GUID
         * @returns {Boolean} True when it has reservations for a product
         */
        function has(productId) {
            return _.some(pool, { productId });
        }


        /**
         * Get the current ticketReservatoin
         * @returns {Reservation} current Ticket Reservation
         */
        function getTicketReservation() {
            return ticket;
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#getPersonMetaDataFor
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Get deduplicated set of applies_to:person metadata for a collection of products, but only if we have
         * reservations for one or more of these products.
         * @param {String} productIds One or more product ids
         * @returns {Array<MetaData>} Array of metadata
         */
        function getPersonMetaDataFor(...productIds) {
            const products = productIds.map(guid => _.find(ticket.products, { guid }));
            const metaData = products.reduce((set, product) => {
                if(!has(product.guid))
                    return set;
                const metaData = $filter('sortByPredecessor')(product.belongsToMany.restore('MetaData'));
                for(let m of metaData) {
                    if(m.applies_to !== 'person')
                        continue;
                    if(set.some(n => n.guid === m.guid))
                        continue;
                    set.push(m);
                }
                return set;
            }, []);
            return metaData;
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#getProductMetaDataFor
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Get set of applies_to:product metadata for a product, but only if we have reservations for this product
         * @param {String} productId A product id
         * @returns {Array<MetaData>} Array of metadata
         */
        function getProductMetaDataFor(productId) {
            if(!has(productId))
                return [];
            const product = _.find(ticket.products, { guid: productId });
            const metaData = product.belongsToMany.restore('MetaData').filter(m => m.applies_to === 'product');
            return metaData;
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#setMetaDataValue
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Store metadata values for one or more products
         * @param {String} metaDataId Guid of metaData
         * @param {Any} value Value for metaData
         * @param {String|Array<String>} productId One or more product ids
         * @param {String} guid ReservationID if metadata applies_to:product for a specific reservation
         */
        function setMetaDataValue(metaDataId, value, productId, guid) {
            const metaData = MetaData.cached[metaDataId];
            const productIds = angular.isArray(productId) ? productId : [productId];
            for(let id of productIds) {
                if(!Product.belongsToManyCache.isCached(id, 'MetaData', metaData))
                    continue;
                for(let reservation of pool) {
                    if(reservation.productId !== id)
                        continue;
                    if(guid && reservation.guid !== guid)
                        continue;
                    reservation.metaData[metaDataId] = value;
                }
            }
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#getPersonMetaDataValuesFor
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Find all values for the `metadata(applies_to:person)` for a set of products. Prefills default values.
         * @param {String} productIds One or more product ids
         * @returns {Object} MetaData values `{metaGuid:value}`
         */
        function getPersonMetaDataValuesFor(...productIds) {
            const values = {};
            const metaData = getPersonMetaDataFor(...productIds);
            for(let meta of metaData) {
                if(meta.default)
                    values[meta.guid] = meta.default;
            }
            for(let reservation of pool) {
                if(!productIds.includes(reservation.productId))
                    continue;
                for(let guid of Object.keys(values)) {
                    // Do we have a default that wasn't saved to the reservation yet
                    if(values[guid] && !reservation.metaData[guid])
                        reservation.metaData[guid] = values[guid];
                }
                for(let guid of Object.keys(reservation.metaData)) {
                    if(MetaData.cached[guid].applies_to !== 'person')
                        continue;
                    values[guid] = reservation.metaData[guid];
                }
            }
            return values;
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#getProductMetaDataValuesFor
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Find all values for the `metadata(applies_to:product)` for a product reservation. Prefills default values.
         * @param {String} productId Product guid
         * @param {String} guid Reservation guid
         * @returns {Object} MetaData values `{metaGuid:value}`
         */
        function getProductMetaDataValuesFor(productId, guid) {
            const values = {};
            const metaData = getProductMetaDataFor(productId);
            for(let meta of metaData) {
                if(meta.default)
                    values[meta.guid] = meta.default;
            }
            for(let reservation of pool) {
                if(reservation.productId !== productId || reservation.guid !== guid)
                    continue;
                for(let metaId of Object.keys(values)) {
                    // Do we have a default that wasn't saved to the reservation yet
                    if(values[metaId] && !reservation.metaData[metaId])
                        reservation.metaData[metaId] = values[metaId];
                }
                for(let metaId of Object.keys(reservation.metaData)) {
                    if(MetaData.cached[metaId].applies_to !== 'product')
                        continue;
                    values[metaId] = reservation.metaData[metaId];
                }
            }
            return values;
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#satisfies
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Check if the minimum, maximum and uniqueness requirements for a group are met
         * @param {ProductGroup} group The product group to check
         * @return {Boolean} True if the group is satisfied
         */
        function satisfies(group) {
            const amounts = _.fromPairs(_.map(group.products, productId => [productId, filter(productId).length]));
            const total = _.sum(_.values(amounts));
            if(total < group.min_bound)
                return false;
            if(group.max_bound && total > group.max_bound)
                return false;
            if(group.uniqueness && _.some(amounts, amount => amount > group.uniqueness))
                return false;
            return true;
        }

        /**
         * @ngdoc method
         * @name eventix.common.models.ProductReservationPool#invalid
         * @methodOf eventix.common.models.ProductReservationPool
         * @description
         * Check if the metaData for all reservations in the pool is valid
         * @return {Boolean} true if the reservation metaData is invalid
         */
        function invalid() {
            const hasErrors = $filter('hasNestedErrors');

            for(let reservation of pool) {
                const product = _.find(ticket.products, { guid: reservation.productId });
                const metaData = product.belongsToMany.restore('MetaData');

                if(!ticket.latePersonalization) {
                    reservation.$errors = LaravelValidationRules.validate(metaData, reservation.metaData);

                    if (hasErrors(reservation.$errors)) {
                        return true;
                    }
                }
            }

            return false;
        }
    };
}
